import React from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';

import { AuthLayout } from '@components';
import {
  AuthNavigatorNavigationPropType,
  AuthNavigatorRoutePropType,
  screens,
} from '@navigation';
import { SignUpSchemaDataType } from '@validation';
import { useAuthModel } from '@models/AuthModel';
import { LocalizationService } from '@services';
import { useAuthErrorHandler } from '../hooks/useAuthErrorHandler';
import { SignUpForm } from './components/SignUpForm';
import { useNavOptionsSet } from '@hooks';

type NavigationPropType = AuthNavigatorNavigationPropType<'SignUp'>;
type RoutePropType = AuthNavigatorRoutePropType<'SignUp'>;

export const SignUpScreen = observer(() => {
  const nav = useNavigation<NavigationPropType>();
  const { params } = useRoute<RoutePropType>();
  const { t } = useTranslation();
  const { handleError } = useAuthErrorHandler();
  const authModel = useAuthModel();

  useNavOptionsSet({ title: t('signUp.title') });

  async function signUp(values: SignUpSchemaDataType) {
    try {
      // Before switching to the next step (form) we check if a user with this
      // email already registered
      await authModel.verifySignUpData.run({ email: values.email });

      authModel.saveSignUpDraft({
        email: values.email,
        password: values.password,
        firstName: values.firstName,
        lastName: values.lastName,
        countryCode: values.country,
        language: LocalizationService.language,
      });

      nav.navigate(screens.ChooseSubscriptionPlan, params);
    } catch (error) {
      handleError({
        error,
        email: values.email,
        onSuccess: () => nav.replace(screens.SignIn),
      });
    }
  }

  function navigateToSignIn() {
    nav.navigate(screens.SignIn, params);
  }

  return (
    <AuthLayout
      headerButtonText={t('signUp.signIn')}
      headerButtonSideText={t('signUp.haveAccount')}
      onPressHeaderButton={navigateToSignIn}
    >
      <SignUpForm onSubmit={signUp} />
    </AuthLayout>
  );
});
